/**
 * Confidence intervals for a mean, a proportion, and a difference between two means.
 *
 * - Mean: x̄ ± t* s/√n on n − 1 df, or x̄ ± z* σ/√n when the population SD is known.
 * - Proportion: Wald, Wilson score, and Clopper–Pearson (exact, from beta quantiles).
 * - Two means: Welch's interval, which does not assume equal variances, with the
 *   Welch–Satterthwaite df.
 *
 * `level` is the confidence level as a proportion (0.95), never a percentage.
 */

import { betaQuantile } from "@/lib/stats/beta-quantile";
import { mean, standardDeviation } from "@/lib/stats/descriptive";
import { normalQuantile, studentTQuantile } from "@/lib/stats/distributions";

export interface Interval {
  estimate: number;
  lower: number;
  upper: number;
  /** Half-width: critical value × standard error. */
  margin: number;
  se: number;
  /** z* or t*. */
  critical: number;
  df?: number;
}

export interface MeanSummary {
  mean: number;
  sd: number;
  n: number;
}

export function isValidLevel(level: number): boolean {
  return Number.isFinite(level) && level > 0 && level < 1;
}

/** Mean, sample SD (n − 1) and n of a data set. */
export function summarize(values: readonly number[]): MeanSummary {
  return {
    mean: mean(values),
    sd: standardDeviation(values),
    n: values.length,
  };
}

export function meanInterval(
  summary: MeanSummary,
  level: number,
  sigmaKnown: boolean
): Interval | null {
  const { n, sd } = summary;
  const minimum = sigmaKnown ? 1 : 2;
  if (!(isValidLevel(level) && Number.isInteger(n) && n >= minimum && sd > 0)) {
    return null;
  }
  const upperP = 1 - (1 - level) / 2;
  const se = sd / Math.sqrt(n);
  const df = sigmaKnown ? undefined : n - 1;
  const critical =
    df === undefined ? normalQuantile(upperP) : studentTQuantile(upperP, df);
  const margin = critical * se;
  return {
    estimate: summary.mean,
    lower: summary.mean - margin,
    upper: summary.mean + margin,
    margin,
    se,
    critical,
    df,
  };
}

export interface ProportionBounds {
  lower: number;
  upper: number;
  /** The raw formula went below 0 or above 1 and was cut back to [0, 1]. */
  clipped: boolean;
}

export interface ProportionIntervals {
  phat: number;
  z: number;
  /** √(p̂(1 − p̂)/n), the Wald standard error. */
  se: number;
  wald: ProportionBounds;
  wilson: ProportionBounds;
  exact: ProportionBounds;
  /** n·p̂ and n·(1 − p̂): the counts behind the "at least 10 of each" rule. */
  successes: number;
  failures: number;
}

function clip(lower: number, upper: number): ProportionBounds {
  return {
    lower: Math.max(0, lower),
    upper: Math.min(1, upper),
    // Rounding dust (−1e-17 for x = 0) is not the formula overshooting.
    clipped: lower < -1e-12 || upper > 1 + 1e-12,
  };
}

export function proportionIntervals(
  x: number,
  n: number,
  level: number
): ProportionIntervals | null {
  if (
    !(
      isValidLevel(level) &&
      Number.isInteger(x) &&
      Number.isInteger(n) &&
      n >= 1 &&
      x >= 0 &&
      x <= n
    )
  ) {
    return null;
  }
  const alpha = 1 - level;
  const z = normalQuantile(1 - alpha / 2);
  const phat = x / n;
  const se = Math.sqrt((phat * (1 - phat)) / n);
  const wald = clip(phat - z * se, phat + z * se);

  const z2 = z * z;
  const denominator = 1 + z2 / n;
  const centre = (phat + z2 / (2 * n)) / denominator;
  const half =
    (z / denominator) * Math.sqrt((phat * (1 - phat)) / n + z2 / (4 * n * n));
  const wilson = clip(centre - half, centre + half);

  const exact = {
    lower: x === 0 ? 0 : betaQuantile(alpha / 2, x, n - x + 1),
    upper: x === n ? 1 : betaQuantile(1 - alpha / 2, x + 1, n - x),
    clipped: false,
  };

  return {
    phat,
    z,
    se,
    wald,
    wilson,
    exact,
    successes: x,
    failures: n - x,
  };
}

export interface WelchInterval extends Interval {
  df: number;
}

export function welchInterval(
  first: MeanSummary,
  second: MeanSummary,
  level: number
): WelchInterval | null {
  const valid = (s: MeanSummary) =>
    Number.isInteger(s.n) && s.n >= 2 && s.sd >= 0 && Number.isFinite(s.mean);
  if (!(isValidLevel(level) && valid(first) && valid(second))) {
    return null;
  }
  const v1 = (first.sd * first.sd) / first.n;
  const v2 = (second.sd * second.sd) / second.n;
  if (v1 + v2 <= 0) {
    return null;
  }
  const se = Math.sqrt(v1 + v2);
  const df =
    (v1 + v2) ** 2 / (v1 ** 2 / (first.n - 1) + v2 ** 2 / (second.n - 1));
  const critical = studentTQuantile(1 - (1 - level) / 2, df);
  const estimate = first.mean - second.mean;
  const margin = critical * se;
  return {
    estimate,
    lower: estimate - margin,
    upper: estimate + margin,
    margin,
    se,
    critical,
    df,
  };
}
